
import React, { useState } from "react";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "../components/ui/form";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, getFirestore, serverTimestamp } from "firebase/firestore";
import app from "../../firebase/firebaseConfig";

const db = getFirestore(app);

const formSchema = z.object({
  restaurantName: z.string().min(2, { message: "Restaurant name is required" }),
  ownerName: z.string().min(2, { message: "Owner name is required" }),
  phone: z.string().min(10, { message: "Valid phone number is required" }),
  email: z.string().email({ message: "Valid email is required" }),
  contactMethod: z.enum(["email", "phone", "either"]),
  boostCardOrder: z.enum(["50", "100", "150", "200"]),
  guaranteeAgreement: z.boolean().refine(val => val === true, {
    message: "You must accept the guarantee terms"
  }),
  paymentChoice: z.enum(["now", "later"]),
  paymentAgreement: z.boolean().optional()
});

type FormValues = z.infer<typeof formSchema>;

const Close = () => {
  const navigate = useNavigate();
  const [cardPrice, setCardPrice] = useState(0.85); // Default price per card
  const [discountPercentage, setDiscountPercentage] = useState(15); // 15% supplier discount
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submittedRestaurantName, setSubmittedRestaurantName] = useState("");

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      restaurantName: "",
      ownerName: "",
      phone: "",
      email: "",
      contactMethod: "email",
      boostCardOrder: "50",
      guaranteeAgreement: false,
      paymentChoice: "later",
      paymentAgreement: false
    }
  });

  const watchPaymentChoice = form.watch("paymentChoice");
  const watchBoostCardOrder = form.watch("boostCardOrder");
  
  const calculateSubtotal = () => {
    switch (watchBoostCardOrder) {
      case "50":
        return 256;
      case "100":
        return 421;
      case "150":
        return 587;
      case "200":
        return 676;
      default:
        return 0;
    }
  };

  const calculatePerCard = () => {
    const quantity = parseInt(watchBoostCardOrder);
    const subtotal = calculateSubtotal();
    return (subtotal / quantity).toFixed(2);
  };
  
  const calculateDiscount = () => {
    switch (watchBoostCardOrder) {
      case "50":
        return 71;
      case "100":
        return 118;
      case "150":
        return 165;
      case "200":
        return 190;
      default:
        return 0;
    }
  };
  
  const calculateTotal = () => {
    return (calculateSubtotal() - calculateDiscount()).toFixed(2);
  };
  
  // For "Later" payment, fixed payable rates per Boost Card sold
  const calculatePayablePerCard = () => {
    switch (watchBoostCardOrder) {
      case "50":
        return "5.78";
      case "100":
        return "4.75";
      case "150":
        return "4.41";
      case "200":
        return "3.81";
      default:
        return "0.00";
    }
  };

  const onSubmit = async (values: FormValues) => {
    console.log("Form submitted:", values);
    try {
      await addDoc(collection(db, "partner_registration"), {
        restaurantName: values.restaurantName,
        ownerName: values.ownerName,
        phone: values.phone,
        email: values.email,
        contactMethod: values.contactMethod,
        boostCardOrder: values.boostCardOrder,
        paymentChoice: values.paymentChoice,
        createdAt: serverTimestamp()
      });
      setSubmittedRestaurantName(values.restaurantName);
      setFormSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  // Success screen component
  if (formSubmitted) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center p-4 sm:p-6 bg-gray-50">
        <div className="w-full max-w-3xl mx-auto bg-white rounded-lg shadow-md p-6 sm:p-8 text-center">
          <div className="flex justify-center mb-6">
            <Logo />
          </div>
          
          <h1 className="text-2xl sm:text-3xl font-bold mb-4">Welcome to EatsBoost {submittedRestaurantName}!</h1>
          
          <p className="text-lg mb-8">
            Thank you for registering as a partner. We're excited to have you on board!
          </p>
          
          <Button 
            onClick={() => navigate("/")}
            className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-full shadow-md transition-colors"
          >
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col p-4 sm:p-6 bg-gray-50">
      <div className="w-full max-w-3xl mx-auto bg-white rounded-lg shadow-md p-6 sm:p-8">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        
        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-6">Partner Registration Form</h1>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="restaurantName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Restaurant Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter restaurant name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="ownerName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Owner Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter owner name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Phone Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter email address" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="contactMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Contact Method</FormLabel>
                  <FormControl>
                    <RadioGroup 
                      className="flex space-x-4" 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="email" id="email" />
                        <label htmlFor="email">Email</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="phone" id="phone" />
                        <label htmlFor="phone">Phone</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="either" id="either" />
                        <label htmlFor="either">Either</label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="boostCardOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Initial Boost Card Order</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select quantity" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-white">
                      <SelectItem value="50">50 Boost Cards</SelectItem>
                      <SelectItem value="100">100 Boost Cards</SelectItem>
                      <SelectItem value="150">150 Boost Cards</SelectItem>
                      <SelectItem value="200">200 Boost Cards</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="paymentChoice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Paying for Boost Cards</FormLabel>
                  <FormControl>
                    <RadioGroup 
                      className="flex space-x-4" 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="now" id="now" />
                        <label htmlFor="now">Upfront</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="later" id="later" />
                        <label htmlFor="later">Later</label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Order Summary block - shown only for "now" payment */}
            {watchPaymentChoice === "now" && (
              <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                <p className="font-medium text-lg">Order Summary</p>
                <div className="mt-2 flex justify-between">
                  <span>{watchBoostCardOrder} Boost Cards @ ${calculatePerCard()} each</span>
                  <span>${calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="mt-2 flex justify-between text-green-600">
                  <span>Supplier Discount</span>
                  <span>-${calculateDiscount().toFixed(2)}</span>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-200 flex justify-between font-bold">
                  <span>Total</span>
                  <span>${calculateTotal()}</span>
                </div>
              </div>
            )}

            {/* Pay As You Sell block for "later" payment */}
            {watchPaymentChoice === "later" && (
  <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mt-4">
    <p className="font-medium text-lg">Pay As You Sell Boost Cards</p>
    <div className="mt-2 flex justify-between">
      <span>Amount payable per Boost Card sold:</span>
      <span className="font-bold">${calculatePayablePerCard()}</span>
    </div>
    <div className="mt-2 flex justify-between text-green-600">
  <span>Supplier Discount</span>
  <span>
    -${
      watchBoostCardOrder === "50" ? "1.44" :
      watchBoostCardOrder === "100" ? "1.19" :
      watchBoostCardOrder === "150" ? "1.10" :
      watchBoostCardOrder === "200" ? "0.95" : "0.00"
    }
  </span>
</div>
<div className="mt-4 pt-4 border-t border-blue-200 flex justify-between font-bold">
  <span>Total</span>
  <span>
    ${(
      parseFloat(calculatePayablePerCard()) -
      (watchBoostCardOrder === "50" ? 1.44 :
       watchBoostCardOrder === "100" ? 1.19 :
       watchBoostCardOrder === "150" ? 1.10 :
       watchBoostCardOrder === "200" ? 0.95 : 0)
    ).toFixed(2)}
  </span>
</div>
    <div className="mt-2">
      <p className="text-sm text-gray-600">
        Based on your initial order of {watchBoostCardOrder} Boost Cards
      </p>
    </div>
  </div>
)}


            {/* Payment Agreement checkbox (appears regardless of payment choice) */}
            <FormField
              control={form.control}
              name="paymentAgreement"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 mt-4">
                  <FormControl>
                    <Checkbox 
                      checked={field.value} 
                      onCheckedChange={field.onChange}
                      className="h-5 w-5 border-2 border-eatsboost-orange"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm">
                      {watchPaymentChoice === "now"
                        ? "I understand that to be eligible for the supplier discount, I must pay the full amount within 3 days"
                        : "I understand that to be eligible for the supplier discount, I must finalize within 3 days of the Demo Meeting"}
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="guaranteeAgreement"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 mt-8 p-3 bg-gray-50 rounded-md border border-gray-200">
                  <FormControl>
                    <Checkbox 
                      checked={field.value} 
                      onCheckedChange={field.onChange}
                      className="h-5 w-5 border-2 border-eatsboost-orange"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-medium">
                      I understand that I am eligible for a 90-day 100% money back guarantee at any time
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <div className="pt-4">
              <Button 
                type="submit" 
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-full shadow-md transition-colors"
              >
                Complete Registration
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default Close;
